'use client';

import { useState, type FormEvent } from 'react';
import { HoneypotField } from '@/components/honeypot-field';
import { FIELD_BASE } from '@/lib/form-styles';
import { HONEYPOT_FIELD } from '@/lib/forms/spam';
import { readTrackingPayload } from '@/lib/forms/tracking-payload';
import { PARTNER_FIELDS as F, PARTNER_FORM_ENDPOINT, PARTNER_TYPES } from '@/lib/forms/partner';

const STATUS = {
  idle: 'idle',
  submitting: 'submitting',
  done: 'done',
  error: 'error',
} as const;
type Status = (typeof STATUS)[keyof typeof STATUS];

const LABEL = 'text-[13px] font-semibold text-brand-navy/80';
const HINT = 'text-xs text-brand-navy/70';

function Label({ htmlFor, children, required }: { htmlFor: string; children: string; required?: boolean }) {
  return (
    <label htmlFor={htmlFor} className={LABEL}>
      {children}
      {required ? <span className="text-red-600"> *</span> : null}
    </label>
  );
}

/** Venue, hotel and charity partner application; confirms inline, never redirects. */
export function PartnerForm() {
  const [status, setStatus] = useState<Status>(STATUS.idle);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(STATUS.submitting);
    setErrorMessage(undefined);

    const data = new FormData(event.currentTarget);
    try {
      const response = await fetch(PARTNER_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          firstName: data.get(F.firstName),
          lastName: data.get(F.lastName),
          email: data.get(F.email),
          phone: data.get(F.phone),
          company: data.get(F.company),
          partnerType: data.get(F.partnerType),
          siteUrl: data.get(F.siteUrl),
          partnerCity: data.get(F.partnerCity),
          details: data.get(F.details),
          [HONEYPOT_FIELD]: data.get(HONEYPOT_FIELD) || undefined,
          tracking: readTrackingPayload(),
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as
          | { error?: string; issues?: Record<string, string[] | undefined> }
          | null;
        const firstIssue = Object.values(body?.issues ?? {}).flat()[0];
        throw new Error(firstIssue ?? body?.error ?? 'Something went wrong');
      }
      setStatus(STATUS.done);
    } catch (error) {
      setStatus(STATUS.error);
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
    }
  }

  if (status === STATUS.done) {
    return (
      <div role="status" className="rounded-xl bg-brand-mist p-6">
        <p className="text-lg font-bold text-brand-royal">Thanks, your application is in.</p>
        <p className="mt-2 text-[15px] leading-relaxed text-brand-navy/75">
          We&rsquo;ll reply within a week, usually with a few questions and a request for a photo and a short description.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.firstName} required>First name</Label>
          <input id={F.firstName} name={F.firstName} required autoComplete="given-name" className={FIELD_BASE} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.lastName} required>Last name</Label>
          <input id={F.lastName} name={F.lastName} required autoComplete="family-name" className={FIELD_BASE} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.email} required>Work email</Label>
          <input id={F.email} name={F.email} type="email" required autoComplete="email" className={FIELD_BASE} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.phone}>Phone</Label>
          <input id={F.phone} name={F.phone} type="tel" autoComplete="tel" className={FIELD_BASE} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.company} required>Venue or organization</Label>
          <input id={F.company} name={F.company} required autoComplete="organization" className={FIELD_BASE} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.partnerType} required>You are a</Label>
          <select id={F.partnerType} name={F.partnerType} required defaultValue={PARTNER_TYPES[0]} className={FIELD_BASE}>
            {PARTNER_TYPES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.siteUrl} required>Website</Label>
          <input id={F.siteUrl} name={F.siteUrl} type="url" required placeholder="https://" autoComplete="url" className={FIELD_BASE} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.partnerCity} required>City and state</Label>
          <input id={F.partnerCity} name={F.partnerCity} required maxLength={120} placeholder="Boston, MA" className={FIELD_BASE} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={F.details}>Tell us about your space or your mission</Label>
        <textarea
          id={F.details}
          name={F.details}
          rows={4}
          maxLength={3000}
          aria-describedby={`${F.details}-hint`}
          className={FIELD_BASE}
        />
        <p id={`${F.details}-hint`} className={HINT}>
          Venues: group size and meeting space. Nonprofits: who you serve and what donations help most.
        </p>
      </div>
      <HoneypotField />
      {status === STATUS.error ? (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {errorMessage}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={status === STATUS.submitting}
        className="self-start rounded-full bg-brand-yellow px-8 py-3.5 text-[15px] font-bold text-brand-navy shadow-sm transition-all hover:-translate-y-px hover:bg-brand-yellow-dark hover:shadow-md disabled:translate-y-0 disabled:opacity-60"
      >
        {status === STATUS.submitting ? 'Sending…' : 'Apply to be a partner'}
      </button>
    </form>
  );
}
